import type { BelegDetails } from './api';
import { formatiereEuro, nettoUndUst, type Steuersatz } from './warenkorb';

/**
 * Rechnung im A4-Format (Firmenkunden, § 11 UStG): alle Rechnungsmerkmale aus
 * dem signierten Beleg – Unternehmer, Empfänger, fortlaufende Nummer, Datum,
 * Leistung mit Menge, Entgelt je Steuersatz mit USt, Kassen-ID und der
 * maschinenlesbare Code. Reines HTML ohne Skript (wird in einem iframe gedruckt); Beträge in Cent.
 */
export interface RechnungUnternehmer {
  name: string;
  anschrift: string;
  uid?: string | undefined;
}

export function htmlEscape(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-AT', { timeZone: 'Europe/Vienna' });
}

function zeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' });
}

const ZAHLUNGSART: Record<string, string> = {
  bar: 'Barzahlung',
  karte: 'Kartenzahlung',
  gutschein: 'Gutschein',
};

/** Vollständiges HTML-Dokument der Rechnung; `qrDataUrl` ist das Bild des Codes oder null. */
export function rechnungHtml(
  d: BelegDetails,
  unternehmer: RechnungUnternehmer,
  qrDataUrl: string | null,
): string {
  const positionen = d.positionen.filter((p) => typeof p.bruttoCent === 'number');
  const jeSatz = new Map<Steuersatz, number>();
  for (const p of positionen) {
    jeSatz.set(p.steuersatz, (jeSatz.get(p.steuersatz) ?? 0) + p.bruttoCent);
  }
  const brutto = positionen.reduce((s, p) => s + p.bruttoCent, 0);
  const storno = d.belegtyp === 'STORNO';
  const titel = storno
    ? 'Stornorechnung'
    : d.belegtyp === 'TRAINING'
      ? 'TRAINING – keine Rechnung'
      : 'Rechnung';
  const zeilen = positionen
    .map(
      (p) =>
        `<tr><td class="r">${String(p.menge)}</td><td>${htmlEscape(p.bezeichnung)}</td><td class="r">${p.steuersatz} %</td><td class="r">${formatiereEuro(
          Math.round(p.bruttoCent / (p.menge === 0 ? 1 : p.menge)),
        )}</td><td class="r">${formatiereEuro(p.bruttoCent)}</td></tr>`,
    )
    .join('');
  const steuern = [...jeSatz]
    .map(([satz, summe]) => {
      const { nettoCent, ustCent } = nettoUndUst(summe, satz);
      return `<tr><td>Entgelt ${satz} %</td><td class="r">${formatiereEuro(nettoCent)}</td><td class="r">${formatiereEuro(ustCent)}</td><td class="r">${formatiereEuro(summe)}</td></tr>`;
    })
    .join('');
  const empfaenger =
    d.empfaenger === null
      ? '<p class="hinweis">Vereinfachte Rechnung ohne Empfängerangabe (§ 11 Abs. 6 UStG)</p>'
      : `<p><strong>Rechnungsempfänger</strong><br>${htmlEscape(d.empfaenger.name)}<br>${htmlEscape(d.empfaenger.anschrift)}${
          d.empfaenger.uid === undefined ? '' : `<br>UID: ${htmlEscape(d.empfaenger.uid)}`
        }</p>`;
  const zusatz: string[] = [];
  if (d.zahlungsart !== null) {
    zusatz.push(`Zahlung: ${ZAHLUNGSART[d.zahlungsart] ?? d.zahlungsart}`);
  }
  if (d.gutscheinCent > 0) {
    zusatz.push(`davon mit Gutschein bezahlt: ${formatiereEuro(d.gutscheinCent)} €`);
  }
  if ((d.trinkgeldCent ?? 0) > 0) {
    zusatz.push(`Trinkgeld (kein Umsatz): ${formatiereEuro(d.trinkgeldCent ?? 0)} €`);
  }
  if (d.bediener !== null) {
    zusatz.push(`Bediener: ${htmlEscape(d.bediener)}`);
  }
  if (d.notiz !== null) {
    zusatz.push(`Notiz: ${htmlEscape(d.notiz)}`);
  }
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${titel} ${htmlEscape(d.belegnummer)}</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Helvetica, Arial, sans-serif; font-size: 11pt; color: #111; margin: 0; }
  header { display: flex; justify-content: space-between; gap: 2rem; margin-bottom: 1.5rem; }
  h1 { font-size: 20pt; margin: 0 0 0.3rem; }
  table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
  th, td { padding: 0.35rem 0.5rem; border-bottom: 1px solid #ccc; text-align: left; vertical-align: top; }
  th { background: #f0f0f0; }
  .r { text-align: right; white-space: nowrap; }
  .summe td { font-weight: 700; border-top: 2px solid #111; }
  .hinweis { color: #555; font-size: 9.5pt; }
  .ausfall { color: #b3261e; font-weight: 700; }
  footer { display: flex; gap: 1.5rem; align-items: flex-start; margin-top: 2rem; font-size: 9pt; color: #333; }
  footer img { width: 38mm; height: 38mm; }
  .code { word-break: break-all; font-family: Menlo, Consolas, monospace; font-size: 7pt; }
</style></head><body>
<header>
  <div><strong>${htmlEscape(unternehmer.name)}</strong><br>${htmlEscape(unternehmer.anschrift)}${
    unternehmer.uid === undefined ? '' : `<br>UID: ${htmlEscape(unternehmer.uid)}`
  }</div>
  <div class="r"><h1>${titel}</h1>Nr. <strong>${htmlEscape(d.belegnummer)}</strong><br>Datum: ${datum(d.zeitpunkt)} ${zeit(d.zeitpunkt)}<br>Kassen-ID: ${htmlEscape(d.kassenId)}</div>
</header>
${empfaenger}
<table>
  <thead><tr><th class="r">Menge</th><th>Leistung</th><th class="r">USt</th><th class="r">Einzel brutto</th><th class="r">Betrag brutto</th></tr></thead>
  <tbody>${zeilen}</tbody>
</table>
<table>
  <thead><tr><th>Steuersatz</th><th class="r">Netto</th><th class="r">USt</th><th class="r">Brutto</th></tr></thead>
  <tbody>${steuern}<tr class="summe"><td>Gesamt</td><td class="r">${formatiereEuro(
    [...jeSatz].reduce((s, [satz, summe]) => s + nettoUndUst(summe, satz).nettoCent, 0),
  )}</td><td class="r">${formatiereEuro(
    [...jeSatz].reduce((s, [satz, summe]) => s + nettoUndUst(summe, satz).ustCent, 0),
  )}</td><td class="r">${formatiereEuro(brutto)} €</td></tr></tbody>
</table>
<p>${zusatz.join(' · ')}</p>
${(d.rechnung?.hinweise ?? []).map((h) => `<p><strong>${htmlEscape(h)}</strong></p>`).join('')}
<p class="hinweis">Leistungsdatum entspricht dem Rechnungsdatum, sofern nicht anders angegeben. Rechnung gemäß § 11 UStG und § 132a BAO; Beleg mit Registrierkasse ${htmlEscape(d.kassenId)} erstellt und signiert (RKSV).</p>
${d.signaturGestoert ? '<p class="ausfall">Sicherheitseinrichtung ausgefallen</p>' : ''}
<footer>
  ${qrDataUrl === null ? '' : `<img src="${qrDataUrl}" alt="Maschinenlesbarer Code">`}
  <div><strong>Maschinenlesbarer Code</strong><div class="code">${htmlEscape(d.qrCode)}</div></div>
</footer>
</body></html>`;
}
